<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import {
    type CustomerUser,
    type JurnodesSettings,
    type JurnodesSettingsRequest,
    Role5ccEnum,
    SettingsService
  } from '@/types/generated'
  import * as Sentry from '@sentry/vue'
  import { useFormWithValidation } from '@/composables/useFormWithValidation'
  import { toast } from '@/components/ui/toast'

  const isLoading = ref(false)
  const { fetchUser } = useUserStore()
  const user = computed((): CustomerUser => useUserStore().user as CustomerUser)

  // Jurnodes integration fields
  const isJurnodesEnabled = ref(false)
  const jurnodesApiUser = ref('')
  const jurnodesApiUrl = ref('')
  const jurnodesApiToken = ref('')
  const originalJurnodesApiToken = ref('')

  const { t } = useTranslation({
    en: {
      warning: 'Warning',
      onlyAdminsAllowedMsg: 'Only the administrator is allowed to edit this setting.',
      // Note: Using {'|'} literal interpolation to display pipe character in "Jur|nodes"
      // Vue i18n treats | as pluralization separator, so we escape it with literal syntax
      description: "Configure Jur{'|'}nodes integration settings for JUPUS.",
      enableJurnodes: "Enable Jur{'|'}nodes Integration",
      jurnodesApiUser: "Jur{'|'}nodes API User",
      jurnodesApiUserPlaceholder: "Enter Jur{'|'}nodes API User",
      jurnodesApiUrl: "Jur{'|'}nodes API URL",
      jurnodesApiUrlPlaceholder: "Enter Jur{'|'}nodes API URL",
      jurnodesApiToken: "Jur{'|'}nodes API Token",
      jurnodesApiTokenPlaceholder: "Enter Jur{'|'}nodes API Token",
      saving: 'Saving...',
      syncJurnodes: "Sync Jur{'|'}nodes",
      success: 'Success',
      syncSuccessMsg: "Jur{'|'}nodes was successfully synced."
    },
    de: {
      warning: 'Achtung',
      onlyAdminsAllowedMsg: 'Nur der Administrator kann diese Einstellung bearbeiten.',
      description: "Die Jur{'|'}nodes Schnittstelle",
      enableJurnodes: "Jur{'|'}nodes Integration aktivieren",
      jurnodesApiUser: "Jur{'|'}nodes API User",
      jurnodesApiUserPlaceholder: "Jur{'|'}nodes API User eingeben",
      jurnodesApiUrl: "Jur{'|'}nodes API URL",
      jurnodesApiUrlPlaceholder: "Jur{'|'}nodes API URL eingeben",
      jurnodesApiToken: "Jur{'|'}nodes API Token",
      jurnodesApiTokenPlaceholder: "Jur{'|'}nodes API Token eingeben",
      saving: 'Speichern...',
      syncJurnodes: "Jur{'|'}nodes synchronisieren",
      success: 'Erfolg',
      syncSuccessMsg: "Jur{'|'}nodes wurde erfolgreich synchronisiert."
    }
  })

  // Validation schema content removed as it is not needed;
  // all fields are optional and have no format rule.
  const validationSchema = {}

  interface FormData {
    jurnodes_api_user: string
    jurnodes_api_url: string
    jurnodes_api_token: string
  }

  const { formData, formErrors, touchedFields, validateField, handleFieldChange } =
    useFormWithValidation<FormData>(
      {
        jurnodes_api_user: '',
        jurnodes_api_url: '',
        jurnodes_api_token: ''
      },
      validationSchema
    )

  const internalJurnodesUpdate = async (data: JurnodesSettingsRequest) => {
    if (!user.value) {
      throw Error('User is not defined. Cannot update Jurnodes settings.')
    }

    await SettingsService.settingsJurnodesCreate({
      requestBody: data
    })
  }

  const updateJurnodesSettings = async () => {
    try {
      isLoading.value = true

      // Create data object with only the fields that have values
      const data: JurnodesSettingsRequest = {
        is_jurnodes_export_integration_enabled: isJurnodesEnabled.value
      }

      // Only include api user if it has a value
      if (jurnodesApiUser.value) {
        data.jurnodes_api_user = jurnodesApiUser.value
      }

      // Only include api url if it has a value
      if (jurnodesApiUrl.value) {
        data.jurnodes_api_url = jurnodesApiUrl.value
      }

      // Only include api token if it has changed
      if (jurnodesApiToken.value !== originalJurnodesApiToken.value) {
        data.jurnodes_api_token = jurnodesApiToken.value
      }

      await internalJurnodesUpdate(data)
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      isLoading.value = false
    }
  }

  onMounted(async () => {
    if (!user.value) {
      await fetchUser()
    }

    try {
      const jurnodesSettings: JurnodesSettings =
        (await SettingsService.settingsJurnodesList()) as JurnodesSettings

      // Initialize form with existing values if available
      if (jurnodesSettings) {
        isJurnodesEnabled.value = jurnodesSettings.is_jurnodes_export_integration_enabled || false
        jurnodesApiUser.value = jurnodesSettings.jurnodes_api_user || ''
        formData.jurnodes_api_user = jurnodesApiUser.value
        jurnodesApiUrl.value = jurnodesSettings.jurnodes_api_url || ''
        formData.jurnodes_api_url = jurnodesApiUrl.value
        jurnodesApiToken.value = jurnodesSettings.jurnodes_api_token || ''
        originalJurnodesApiToken.value = jurnodesSettings.jurnodes_api_token || ''
        formData.jurnodes_api_token = jurnodesApiToken.value
      }
    } catch (error) {
      console.error('Error loading Jurnodes settings:', error)
      Sentry.captureException(error)
    }
  })

  // Watch for changes to update the UI accordingly
  watch([isJurnodesEnabled], () => {
    // Only update if we have a user and the component is mounted
    if (user.value) {
      updateJurnodesSettings()
    }
  })

  // Add watchers for the input fields
  watch(jurnodesApiUser, (newValue) => {
    formData.jurnodes_api_user = newValue
    handleFieldChange('jurnodes_api_user', newValue)
    if (user.value) {
      updateJurnodesSettings()
    }
  })

  watch(jurnodesApiUrl, (newValue) => {
    formData.jurnodes_api_url = newValue
    handleFieldChange('jurnodes_api_url', newValue)
    if (user.value) {
      updateJurnodesSettings()
    }
  })

  watch(jurnodesApiToken, (newValue) => {
    formData.jurnodes_api_token = newValue
    handleFieldChange('jurnodes_api_token', newValue)
    if (user.value) {
      updateJurnodesSettings()
    }
  })

  const isJurnodesSyncable = computed(() => {
    return (
      isJurnodesEnabled.value &&
      jurnodesApiUser.value &&
      jurnodesApiUrl.value &&
      jurnodesApiToken.value
    )
  })

  const syncJurnodes = async () => {
    try {
      await SettingsService.settingsJurnodesSyncUpdate()
      toast({
        title: t('success'),
        description: t('syncSuccessMsg'),
        variant: 'default'
      })
    } catch (error) {
      Sentry.captureException(error)
      toast({
        title: 'Error',
        description: 'Jur|nodes snyc failed',
        variant: 'destructive'
      })
    }
  }
</script>

<template>
  <SettingsPage v-if="user">
    <div class="space-y-8">
      <div
        v-if="user.role !== Role5ccEnum.ADMIN"
        class="mb-6"
      >
        <div class="mb-4 w-full rounded-md bg-orange-100 p-4 text-orange-700">
          <p class="font-medium">{{ t('warning') }}</p>
          <p class="mt-2">{{ t('onlyAdminsAllowedMsg') }}</p>
        </div>
      </div>
      <div
        v-else
        class="mb-6"
      >
        <!-- Jurnodes Integration Settings -->
        <div class="max-w-xl space-y-4">
          <!-- Enable Jurnodes Integration -->
          <CheckboxField
            v-model="isJurnodesEnabled"
            name="is_jurnodes_export_integration_enabled"
            :label="t('enableJurnodes')"
          />
          <!-- Jurnodes settings (only shown if Jurnodes is enabled) -->
          <div v-if="isJurnodesEnabled">
            <InputField
              v-model="jurnodesApiUser"
              name="jurnodes_api_user"
              :label="t('jurnodesApiUser')"
              :placeholder="t('jurnodesApiUserPlaceholder')"
            />

            <InputField
              v-model="jurnodesApiUrl"
              name="jurnodes_api_url"
              :label="t('jurnodesApiUrl')"
              :placeholder="t('jurnodesApiUrlPlaceholder')"
            />

            <InputField
              v-model="jurnodesApiToken"
              name="jurnodes_api_token"
              :label="t('jurnodesApiToken')"
              :placeholder="t('jurnodesApiTokenPlaceholder')"
            />

            <button
              class="mt-5 inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
                text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
                focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
              :disabled="!isJurnodesSyncable"
              @click="syncJurnodes"
            >
              {{ t('syncJurnodes') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </SettingsPage>
</template>

<style scoped>
  /* Add any scoped styles here if needed */
</style>
